/* Riconosce gli URL dentro un testo libero e restituisce dei *segmenti*,
 * non dell'HTML.
 *
 * L'implementazione originale (`niceLinks`) costruiva HTML da interpolare con
 * `v-html`:
 *
 *     escapeHTML(text).replace(reURL, '<a href="$&" target="_blank">$1</a>')
 *
 * L'escape passava per `innerText` -> `innerHTML`, che agisce in contesto
 * *text node* e quindi non tocca le virgolette doppie; la regex del path
 * (`[^\s,.<>]`) le lasciava passare. Il risultato è che una descrizione come
 *
 *     https://x.com/a"onmouseover=alert(1)
 *
 * chiudeva l'attributo href e ne apriva uno nuovo:
 *
 *     <a href="https://x.com/a"onmouseover=alert(1)" ...>
 *
 * XSS stored, innescato al passaggio del mouse. In un'app dove le descrizioni
 * arrivano da Pinterest è esattamente la superficie da non lasciare aperta.
 *
 * Qui non si genera HTML: il componente cicla sui segmenti e li rende con
 * l'interpolazione normale di Vue, che nel testo escapa tutto e negli
 * attributi lega i valori senza mai concatenare stringhe. Nessun `v-html`
 * nell'intera applicazione.
 */

// Niente `.` e `,` finali dentro il match: quasi sempre sono punteggiatura
// della frase, non parte dell'URL.
const URL_RE = /https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)]/gi;

const SAFE_PROTOCOLS = new Set(['http:', 'https:']);

/** Etichetta breve per un link: l'host senza `www.`. */
function hostLabel(href) {
  try {
    return new URL(href).host.replace(/^www\./, '');
  } catch {
    return href;
  }
}

/** Un href è sicuro solo se è davvero http/https. Blocca `javascript:`. */
export function isSafeHref(href) {
  try {
    return SAFE_PROTOCOLS.has(new URL(href).protocol);
  } catch {
    return false;
  }
}

/**
 * @param {string} text
 * @returns {Array<{type: 'text'|'link', value?: string, href?: string, label?: string}>}
 */
export function linkify(text) {
  if (!text) return [];

  const segments = [];
  let cursor = 0;

  for (const match of text.matchAll(URL_RE)) {
    const href = match[0];
    if (match.index > cursor) {
      segments.push({ type: 'text', value: text.slice(cursor, match.index) });
    }
    if (isSafeHref(href)) {
      segments.push({ type: 'link', href, label: hostLabel(href) });
    } else {
      segments.push({ type: 'text', value: href });
    }
    cursor = match.index + href.length;
  }

  if (cursor < text.length) {
    segments.push({ type: 'text', value: text.slice(cursor) });
  }
  return segments;
}
